/**
 * Event dispatcher for routing events to the active observability provider.
 * 
 * This module handles the low-level event delivery logic, including:
 * - Provider selection based on configuration
 * - SSR vs Client-side detection
 * - Graceful error handling
 * - Development mode logging
 * 
 * WHY CLIENT-SIDE ONLY:
 * The observability service is designed for client-side tracking (NewRelic browser agent).
 * SSR events (like ExperimentDecision during circuit breaker evaluation) are logged
 * for debugging but not sent to NewRelic. The actual user impact is tracked client-side
 * via ExperimentViewed when components mount.
 */

import { getObservabilityConfig } from '../config';
import { ConsoleObservabilityProvider } from '../providers/console-provider';
import { NewRelicObservabilityProvider } from '../providers/newrelic-provider';
import { ObservabilityProvider } from '../providers/types';

/**
 * Singleton instance of the active provider.
 * Lazy-initialized on first use.
 */
let activeProvider: ObservabilityProvider | null = null;

function getActiveProvider(): ObservabilityProvider {
  if (activeProvider) {
    return activeProvider;
  }

  const config = getObservabilityConfig();

  switch (config.provider) {
    case 'newrelic':
      activeProvider = new NewRelicObservabilityProvider();
      break;
    case 'console':
      activeProvider = new ConsoleObservabilityProvider();
      break;
    default:
      // Fallback to console provider if configuration is invalid
      console.warn('[Observability] Unknown provider type, falling back to console');
      activeProvider = new ConsoleObservabilityProvider();
  }

  return activeProvider;
}

export function dispatchEvent(eventName: string, attributes: Record<string, unknown>): void {
  const config = getObservabilityConfig();
  const isSSR = typeof window === 'undefined';

  // Log in dev mode for debugging
  if (config.enableDevMode) {
    const context = isSSR ? 'SSR' : 'Client';
    console.log(
      `%c[Observability:${context}]%c ${eventName}`,
      'color: #7c3aed; font-weight: bold',
      'color: inherit',
      attributes
    );
  }

  // Server-side: Only log, don't send to NewRelic
  // The client-side ExperimentViewed event will track actual user impact
  if (isSSR) {
    console.debug(`[Observability:SSR] ${eventName}`, attributes);
    return;
  }

  // Client-side: Send to configured provider
  try {
    const provider = getActiveProvider();

    if (!provider.isAvailable()) {
      console.debug(
        `[Observability] Provider "${config.provider}" is not available. Event not tracked:`,
        eventName
      );
      return;
    }

    provider.trackEvent(eventName, attributes);
  } catch (error) {
    // Never let observability failures impact the user experience
    console.error('[Observability] Failed to dispatch event:', eventName, error);
  }
}

export function resetProvider(): void {
  activeProvider = null;
}

